'use client';

/**
 * EventForwardConfigModal — configuration dialog for event forwarding.
 *
 * Two modes (user picks one):
 * - "Via Proxy":   Browser → Proxy Server (?target=url) → Target → Response
 * - "Via Browser": Browser → Target directly → Response (requires CORS)
 *
 * The selected mode + URLs are saved by the parent via the onSave callback.
 */

import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import type { ForwardMode } from '@/constant/eventForwardSamples';
import { explorerAnalytics } from '@/lib/analytics';
import { Button } from '@components/components/ui/button';
import { cn } from '@components/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/shadcn/dialog';
import { AlertTriangle } from 'lucide-react';

const DEFAULT_ORIGIN = 'https://api-explorer.chargebee.com';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ForwardConfig {
  mode: ForwardMode;
  proxyUrl: string;
  targetUrl: string;
}

interface EventForwardConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (config: ForwardConfig) => void;
  initialConfig: ForwardConfig;
}

// ─── Mode Options ───────────────────────────────────────────────────────────

const MODES: { value: ForwardMode; label: string; description: string }[] = [
  {
    value: 'proxy',
    label: 'Via Proxy',
    description: 'Forward through a local proxy server',
  },
  {
    value: 'direct',
    label: 'Via Browser',
    description: 'Send directly from the browser (requires CORS)',
  },
];

// ─── Main Component ─────────────────────────────────────────────────────────

export const EventForwardConfigModal: React.FC<
  EventForwardConfigModalProps
> = ({ open, onOpenChange, onSave, initialConfig }) => {
  // Draft state — only persisted on Save
  const [mode, setMode] = useState<ForwardMode>(initialConfig.mode);
  const [proxyUrl, setProxyUrl] = useState(initialConfig.proxyUrl);
  const [targetUrl, setTargetUrl] = useState(initialConfig.targetUrl);
  // Derive origin once at the modal level to avoid duplicate typeof-window guards
  const [origin, setOrigin] = useState(DEFAULT_ORIGIN);
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);

  // Reset draft to initial values only when the modal opens (not on every render)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setMode(initialConfig.mode);
      setProxyUrl(initialConfig.proxyUrl);
      setTargetUrl(initialConfig.targetUrl);
    }
    prevOpenRef.current = open;
  }, [open, initialConfig]);

  const isValid =
    mode === 'proxy'
      ? proxyUrl.trim() !== '' && targetUrl.trim() !== ''
      : targetUrl.trim() !== '';

  const handleSave = () => {
    onSave({ mode, proxyUrl: proxyUrl.trim(), targetUrl: targetUrl.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle>Forward Configuration</DialogTitle>
          <DialogDescription>
            Choose how to forward events to your endpoint.
          </DialogDescription>
        </DialogHeader>

        {/* ── Mode selector (radio-like buttons) ── */}
        <div className="flex gap-2">
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => {
                if (m.value !== mode) {
                  explorerAnalytics.eventForwardModeChanged({
                    fromMode: mode,
                    toMode: m.value,
                  });
                }
                setMode(m.value);
              }}
              className={cn(
                'flex-1 rounded-md border px-4 py-3 text-left transition-colors',
                mode === m.value
                  ? 'border-shade-teal-300 bg-shade-teal-50'
                  : 'border-gray-200 hover:bg-gray-50'
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    'h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center',
                    mode === m.value
                      ? 'border-shade-teal-600'
                      : 'border-gray-300'
                  )}
                >
                  {mode === m.value && (
                    <div className="h-1.5 w-1.5 rounded-full bg-shade-teal-600" />
                  )}
                </div>
                <span
                  className={cn(
                    'text-sm font-medium',
                    mode === m.value ? 'text-shade-teal-700' : 'text-gray-700'
                  )}
                >
                  {m.label}
                </span>
              </div>
              <p className="mt-1 ml-5.5 text-xs text-gray-500">
                {m.description}
              </p>
            </button>
          ))}
        </div>

        {/* ── Mode-specific content ── */}
        {mode === 'proxy' ? (
          <ProxyModeContent
            proxyUrl={proxyUrl}
            targetUrl={targetUrl}
            onProxyUrlChange={setProxyUrl}
            onTargetUrlChange={setTargetUrl}
          />
        ) : (
          <DirectModeContent
            targetUrl={targetUrl}
            onTargetUrlChange={setTargetUrl}
            origin={origin}
          />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid}
            className="bg-shade-teal-600 text-white hover:bg-shade-teal-700"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Via Proxy Content ──────────────────────────────────────────────────────

const INPUT_CLASS =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-shade-teal-400 focus:outline-none focus:ring-1 focus:ring-shade-teal-400';

const ProxyModeContent: React.FC<{
  proxyUrl: string;
  targetUrl: string;
  onProxyUrlChange: (v: string) => void;
  onTargetUrlChange: (v: string) => void;
}> = ({ proxyUrl, targetUrl, onProxyUrlChange, onTargetUrlChange }) => (
  <div className="space-y-4">
    {/* Flow diagram */}
    <div className="flex items-center gap-2 text-xs text-gray-400">
      <span>Browser</span>
      <span>→</span>
      <span className="font-medium text-gray-600">Proxy</span>
      <span>→</span>
      <span>Target</span>
      <span>→</span>
      <span>Response</span>
    </div>

    <div className="space-y-1.5">
      <label htmlFor="proxy-url" className="text-sm font-medium text-gray-700">
        Proxy Server URL
      </label>
      <input
        id="proxy-url"
        type="url"
        value={proxyUrl}
        onChange={(e) => onProxyUrlChange(e.target.value)}
        placeholder="http://localhost:8000"
        className={INPUT_CLASS}
        data-pendo-mask
      />
    </div>

    <div className="space-y-1.5">
      <label
        htmlFor="proxy-target-url"
        className="text-sm font-medium text-gray-700"
      >
        Target Service URL
      </label>
      <input
        type="url"
        id="proxy-target-url"
        value={targetUrl}
        onChange={(e) => onTargetUrlChange(e.target.value)}
        placeholder="http://my-app.com/webhooks"
        className={INPUT_CLASS}
        data-pendo-mask
      />
      <p className="text-[11px] text-gray-400 leading-relaxed">
        Basic Auth: include credentials in the URL as{' '}
        <code className="bg-gray-100 px-1 rounded font-mono text-[10px]">
          https://user:password@host/path
        </code>
      </p>
    </div>
  </div>
);

// ─── Via Browser Content ────────────────────────────────────────────────────

const DirectModeContent: React.FC<{
  targetUrl: string;
  onTargetUrlChange: (v: string) => void;
  origin: string;
}> = ({ targetUrl, onTargetUrlChange, origin }) => (
  <div className="space-y-4">
    {/* Flow diagram */}
    <div className="flex items-center gap-2 text-xs text-gray-400">
      <span>Browser</span>
      <span>→</span>
      <span className="font-medium text-gray-600">Target</span>
      <span>→</span>
      <span>Response</span>
    </div>

    <div className="space-y-1.5">
      <label
        htmlFor="direct-target-url"
        className="text-sm font-medium text-gray-700"
      >
        Target Service URL
      </label>
      <input
        type="url"
        id="direct-target-url"
        value={targetUrl}
        onChange={(e) => onTargetUrlChange(e.target.value)}
        placeholder="http://localhost:8000/webhooks"
        className={INPUT_CLASS}
        data-pendo-mask
      />
    </div>

    {/* CORS warning */}
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center gap-2 text-sm font-medium text-amber-700">
        <AlertTriangle className="h-4 w-4" />
        CORS headers required
      </div>
      <div className="mt-2 space-y-2 text-xs text-amber-600">
        <p>
          Your server must respond to preflight (OPTIONS) requests and include
          these headers on all responses:
        </p>
        <pre className="rounded border border-amber-200 bg-amber-100/50 p-2 font-mono text-xs">
          {`Access-Control-Allow-Origin: ${origin}
Access-Control-Allow-Methods: POST, OPTIONS
Access-Control-Allow-Headers: Content-Type`}
        </pre>
        <p>
          Without these headers, the browser will block the response and you
          will see a &quot;Failed to fetch&quot; error — even though your server
          may have received the event.
        </p>
      </div>
    </div>
  </div>
);
